import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, Bug, X, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { realtimeService } from '../../lib/realtimeService';
import { useAuthStore } from '../../store/authStore';

interface RealtimeDebugPanelProps {
  conversationId?: string;
  onClose: () => void;
}

export const RealtimeDebugPanel: React.FC<RealtimeDebugPanelProps> = ({
  conversationId,
  onClose
}) => {
  const { user } = useAuthStore();
  const [connectionStatus, setConnectionStatus] = useState('Unknown');
  const [activeChannels, setActiveChannels] = useState<string[]>([]);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setConnectionStatus(realtimeService.getConnectionStatus());
      setActiveChannels(realtimeService.getActiveChannels());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setTestResult(null);

    try {
      const isConnected = await realtimeService.testConnection();
      setTestResult(isConnected ? 'Connection successful!' : 'Connection failed!');
    } catch (error) {
      setTestResult(`Connection error: ${error}`);
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleForceReconnect = async () => {
    try {
      await realtimeService.forceReconnect();
      setTestResult('Reconnection initiated');
    } catch (error) {
      setTestResult(`Reconnection failed: ${error}`);
    }
  };

  const isConnected = connectionStatus === 'Connected';

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-80 z-50 max-h-96 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Bug className="h-5 w-5 mr-2 text-purple-600" />
          <h3 className="text-sm font-medium">Realtime Debug</h3>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3">
        {/* Replication Status Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-start">
            <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-yellow-800">Table Replication Unavailable</p>
              <p className="text-xs text-yellow-700 mt-1">
                Using broadcast events + polling as fallback
              </p>
            </div>
          </div>
        </div>

        {/* Connection Status */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Connection:</span>
          <div className="flex items-center">
            {isConnected ? (
              <Wifi className="h-4 w-4 text-green-500 mr-1" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-500 mr-1" />
            )}
            <span className={`text-sm ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
              {connectionStatus}
            </span>
          </div>
        </div>

        {/* User Info */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">User ID:</span>
          <span className="text-xs text-gray-800 font-mono">
            {user?.id?.substring(0, 8)}...
          </span>
        </div>

        {/* Conversation ID */}
        {conversationId && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Conversation:</span>
            <span className="text-xs text-gray-800 font-mono">
              {conversationId.substring(0, 8)}...
            </span>
          </div>
        )}

        {/* Active Channels */}
        <div>
          <span className="text-sm text-gray-600">Active Channels ({activeChannels.length}):</span>
          <div className="mt-1 max-h-20 overflow-y-auto">
            {activeChannels.length > 0 ? (
              activeChannels.map((channel, index) => (
                <div key={index} className="text-xs text-gray-700 font-mono bg-gray-50 px-2 py-1 rounded mb-1">
                  {channel}
                </div>
              ))
            ) : (
              <div className="text-xs text-gray-500 italic">No active channels</div>
            )}
          </div>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`text-xs p-2 rounded flex items-center ${
            testResult.includes('successful') 
              ? 'bg-green-50 text-green-700' 
              : 'bg-red-50 text-red-700'
          }`}>
            {testResult.includes('successful') ? (
              <CheckCircle className="h-3 w-3 mr-1" />
            ) : (
              <AlertTriangle className="h-3 w-3 mr-1" />
            )}
            {testResult}
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleTestConnection}
            isLoading={isTestingConnection}
            className="flex-1"
          >
            Test Connection
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleForceReconnect}
            className="flex-1"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Reconnect
          </Button>
        </div>

        {/* How It Works */}
        <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
          <strong>How it works without replication:</strong>
          <ul className="mt-1 space-y-1">
            <li>• Broadcast events for real-time updates</li>
            <li>• Polling every 2s for missed messages</li>
            <li>• Presence tracking for online status</li>
            <li>• Typing indicators via broadcasts</li>
          </ul>
        </div>

        {/* Troubleshooting */}
        <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
          <strong>Troubleshooting:</strong>
          <ul className="mt-1 space-y-1">
            <li>• Check if Realtime is enabled in Supabase</li>
            <li>• Verify RLS policies allow access</li>
            <li>• Check browser console for errors</li>
            <li>• Messages may take 2-3 seconds to appear</li>
          </ul>
        </div>
      </div>
    </div>
  );
};